/** Product create/edit form с tabs (general / images / variants / metadata).
 *
 * "Основное" объединяет то, что раньше было раскидано по двум вкладкам
 * (Основное + Переводы) — порядок внутри специально «сверху вниз по
 * приоритету заполнения»: 1) название/описание на RU, 2) переводы на
 * uz/en, 3) технические параметры (SKU, slug, категория, цена, ...).
 * Раньше человек не понимал, с чего начинать — начинал с полей вроде SKU.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Lock, RefreshCw, Unlock } from "lucide-react";
import { lazy, Suspense, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { AttributesTab } from "@/components/products/AttributesTab";
import {
  PrimaryCategoryPicker,
  SecondaryCategoriesPicker,
} from "@/components/products/CategoryPicker";
import { CompetitorPricesTab } from "@/components/products/CompetitorPricesTab";
import { ImageUploader } from "@/components/products/ImageUploader";
import { StoreStockEditor } from "@/components/products/StoreStockEditor";
import { VariantsEditor } from "@/components/products/VariantsEditor";
import { StickySaveBar } from "@/components/forms/StickySaveBar";
import { TranslateButton } from "@/components/forms/TranslateButton";
import { PriceInput } from "@/components/shared/PriceInput";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { slugify } from "@/lib/slugify";

const RichEditor = lazy(() => import("@/components/products/RichEditor"));

const langs = ["ru", "uz", "en"] as const;

// Тот же порядок, что и в STORE_NAMES на бэкенде (competitor_pricing_service.py)
// и в нумерации вкладки «Цены» — 1-4, чтобы владелец мог сослаться "конкурент №2".
const COMPETITOR_STORES = [
  { code: "texnomart", label: "1 — Texnomart" },
  { code: "mediapark", label: "2 — Mediapark" },
  { code: "castore", label: "3 — CA Store" },
  { code: "idea", label: "4 — Idea.uz" },
] as const;

const schema = z.object({
  sku: z.string().min(1).max(120),
  // Код номенклатуры 1С — сквозной ключ для выгрузок и будущей интеграции.
  // Пустая строка = «кода нет» (бэкенд пишет NULL); null нельзя — PATCH
  // отбрасывает None и поле было бы невозможно очистить.
  code_1c: z.string().max(120),
  slug: z.string().min(1).regex(/^[a-z0-9-]+$/, "Только латиница, цифры и дефис"),
  category_id: z.coerce.number().int().positive(),
  // Доп. категории — товар показывается в них, но canonical = primary.
  secondary_category_ids: z.array(z.number().int().positive()).default([]),
  base_price: z.coerce.number().int().nonnegative(),
  sale_price: z.coerce.number().int().nonnegative().nullable(),
  cost_price: z.coerce.number().int().nonnegative().nullable(),
  auto_price_enabled: z.boolean(),
  competitor_discount_percent: z.coerce.number().int().min(0).max(90).nullable(),
  competitor_price_source: z
    .enum(["texnomart", "mediapark", "castore", "idea"])
    .nullable(),
  stock_quantity: z.coerce.number().int().nonnegative(),
  weight_grams: z.coerce.number().int().nonnegative().nullable(),
  is_active: z.boolean(),
  sort_order: z.coerce.number().int(),
  metadata: z.record(z.string(), z.any()),
  translations: z.record(
    z.enum(langs),
    z.object({
      name: z.string().min(1),
      short_description: z.string().nullable().optional(),
      description: z.string().nullable().optional(),
    }),
  ),
}).superRefine((v, ctx) => {
  if (!v.auto_price_enabled) return;
  // Раньше одна общая ошибка вешалась на поле «Себестоимость», даже когда
  // себестоимость заполнена, а не хватало конкурента — менеджер видел
  // «ошибку себестоимости» на карточках, где ИИ-агент не нашёл товар у
  // конкурентов. Теперь каждая ошибка указывает на своё поле.
  if (v.cost_price == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["cost_price"],
      message: "Для автоцены нужна себестоимость",
    });
  }
  if (v.competitor_price_source == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["competitor_price_source"],
      message: "Выберите конкурента, от цены которого считать",
    });
  }
  if (v.competitor_discount_percent == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["competitor_discount_percent"],
      message: "Укажите процент скидки от цены конкурента",
    });
  }
});

type FormValues = z.infer<typeof schema>;

export function ProductEditPage(): JSX.Element {
  const { id } = useParams<{ id?: string }>();
  const isEdit = id !== "new";
  const productId = isEdit && id ? parseInt(id, 10) : null;

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Slug авто-генерируется из RU-названия, пока не начали редактировать его
  // руками (как в InlineCreateDialog для категорий). При редактировании уже
  // существующего товара — стартуем заблокированным, чтобы не перезаписать
  // «живой» slug при правке названия.
  const [slugLocked, setSlugLocked] = useState(Boolean(productId));

  const { data: product, isLoading } = useQuery({
    queryKey: ["admin-product", productId],
    queryFn: async () => {
      if (!productId) return null;
      const { data } = await api.get(`/products/${productId}`);
      return data;
    },
    enabled: Boolean(productId),
  });

  // Secondary категории грузим отдельно — primary уже в product.category_id.
  const { data: categoriesInfo } = useQuery({
    queryKey: ["admin-product-categories", productId],
    queryFn: async () => {
      if (!productId) return null;
      const { data } = await api.get<{
        primary_id: number;
        secondary_ids: number[];
      }>(`/products/${productId}/categories`);
      return data;
    },
    enabled: Boolean(productId),
  });

  const { register, handleSubmit, reset, setValue, watch, formState } =
    useForm<FormValues>({
      resolver: zodResolver(schema),
      defaultValues: {
        sku: "",
        code_1c: "",
        slug: "",
        category_id: 1,
        secondary_category_ids: [],
        base_price: 0,
        sale_price: null,
        cost_price: null,
        auto_price_enabled: false,
        competitor_discount_percent: null,
        competitor_price_source: null,
        stock_quantity: 0,
        weight_grams: null,
        is_active: false,
        sort_order: 0,
        metadata: {},
        translations: {
          ru: { name: "", short_description: null, description: null },
          uz: { name: "", short_description: null, description: null },
          en: { name: "", short_description: null, description: null },
        },
      },
    });

  useEffect(() => {
    if (product) {
      const trMap: any = {};
      for (const tr of product.translations ?? []) {
        trMap[tr.language] = {
          name: tr.name,
          short_description: tr.short_description,
          description: tr.description,
        };
      }
      for (const lang of langs) {
        if (!trMap[lang]) trMap[lang] = { name: "", short_description: null, description: null };
      }
      reset({
        sku: product.sku,
        code_1c: product.code_1c ?? "",
        slug: product.slug,
        category_id: product.category_id,
        secondary_category_ids: categoriesInfo?.secondary_ids ?? [],
        base_price: product.base_price,
        sale_price: product.sale_price,
        cost_price: product.cost_price,
        auto_price_enabled: product.auto_price_enabled ?? false,
        competitor_discount_percent: product.competitor_discount_percent,
        competitor_price_source: product.competitor_price_source ?? null,
        stock_quantity: product.stock_quantity,
        weight_grams: product.weight_grams,
        is_active: product.is_active,
        sort_order: product.sort_order,
        metadata: product.metadata ?? {},
        translations: trMap,
      });
    }
  }, [product, categoriesInfo, reset]);

  const ruName = watch("translations.ru.name");
  useEffect(() => {
    if (slugLocked) return;
    setValue("slug", slugify(ruName ?? ""), { shouldDirty: true });
  }, [ruName, slugLocked, setValue]);

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const { translations, secondary_category_ids, ...base } = values;
      const trArray = Object.entries(translations).map(([language, data]) => ({
        language,
        ...data,
      }));

      let savedId: number;
      if (!productId) {
        const { data } = await api.post("/products", {
          ...base,
          translations: trArray,
        });
        savedId = data.id as number;
      } else {
        await api.patch(`/products/${productId}`, base);
        for (const tr of trArray) {
          if (tr.name) {
            await api.post(`/products/${productId}/translations`, tr);
          }
        }
        savedId = productId;
      }

      // Multi-category sync: primary + secondary в одном PUT
      await api.put(`/products/${savedId}/categories`, {
        primary_id: base.category_id,
        secondary_ids: secondary_category_ids ?? [],
      });

      return savedId;
    },
    onSuccess: (id) => {
      toast.success("Сохранено");
      queryClient.invalidateQueries({ queryKey: ["admin-product", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-product-categories", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      if (!productId) navigate(`/products/${id}/edit`);
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.error?.message ?? "Ошибка");
    },
  });

  const repriceMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/products/${productId}/reprice-now`);
      return data as { base_price: number; base_price_recomputed: boolean; hit_cost_floor: boolean };
    },
    onSuccess: (data) => {
      if (data.base_price_recomputed) {
        setValue("base_price", data.base_price, { shouldDirty: false });
        toast.success(
          data.hit_cost_floor
            ? "Цена пересчитана — упёрлась в себестоимость + мин. наценку"
            : "Цена пересчитана по ценам конкурентов",
        );
      } else {
        toast.error("Нет цен конкурентов для пересчёта — привяжите ссылки на вкладке «Цены»");
      }
      queryClient.invalidateQueries({ queryKey: ["admin-competitor-prices", productId] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? "Не удалось пересчитать цену"),
  });

  if (isEdit && isLoading) {
    return <Skeleton className="h-96" />;
  }

  // Заголовок: показываем русское название товара (если есть), а SKU — как
  // подзаголовок мелкой строкой. Раньше показывался только SKU — это было
  // непонятно: «Редактирование: INS-1646744481» вместо реального имени.
  const productNameRu =
    product?.translations?.find((t: any) => t.language === "ru")?.name ??
    product?.translations?.[0]?.name ??
    null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            {isEdit ? (
              <>
                <div className="truncate">
                  {productNameRu ?? product?.sku ?? "..."}
                </div>
                {productNameRu && product?.sku && (
                  <div className="mt-0.5 text-xs font-normal text-muted-foreground">
                    SKU: {product.sku}
                  </div>
                )}
              </>
            ) : (
              "Новый товар"
            )}
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent>
        <form
          onSubmit={handleSubmit((v) => saveMutation.mutate(v))}
          className="space-y-4"
        >
          <Tabs defaultValue="general">
            <TabsList>
              <TabsTrigger value="general">Основное</TabsTrigger>
              <TabsTrigger value="images" disabled={!productId}>
                Изображения
              </TabsTrigger>
              <TabsTrigger value="variants" disabled={!productId}>
                Варианты
              </TabsTrigger>
              <TabsTrigger value="attributes" disabled={!productId}>
                Характеристики
              </TabsTrigger>
              <TabsTrigger value="stock" disabled={!productId}>
                Остатки
              </TabsTrigger>
              <TabsTrigger value="prices" disabled={!productId}>
                Цены
              </TabsTrigger>
              <TabsTrigger value="metadata">Метаданные</TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-6 pt-4">
              {/* 1. Название и описание (RU) — с этого начинаем заполнение */}
              <div className="space-y-3 p-4 border rounded-md">
                <Label className="font-semibold">1. Название и описание</Label>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Название *</Label>
                  <Input
                    placeholder="Например: Холодильник Samsung RB37"
                    {...register("translations.ru.name")}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    Короткое описание
                  </Label>
                  <Textarea
                    placeholder="Короткое описание — показывается в карточке товара"
                    {...register("translations.ru.short_description")}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    Полное описание
                  </Label>
                  <Suspense fallback={<Skeleton className="h-48" />}>
                    <RichEditor
                      value={watch("translations.ru.description") ?? ""}
                      onChange={(html) =>
                        setValue("translations.ru.description", html, {
                          shouldDirty: true,
                        })
                      }
                      placeholder="Полное описание"
                    />
                  </Suspense>
                </div>
              </div>

              {/* 2. Перевод на другие языки */}
              <div className="space-y-3">
                <Label className="font-semibold">
                  2. Перевод на другие языки
                </Label>
                {langs
                  .filter((lang) => lang !== "ru")
                  .map((lang) => (
                    <div key={lang} className="space-y-2 p-4 border rounded-md">
                      <div className="flex items-center justify-between">
                        <Label className="font-semibold uppercase">{lang}</Label>
                        <TranslateButton
                          sourceText={watch("translations.ru.name") ?? ""}
                          targetLang={lang as "uz" | "en"}
                          onTranslated={(text) =>
                            setValue(`translations.${lang}.name`, text, {
                              shouldDirty: true,
                            })
                          }
                        />
                      </div>
                      <Input
                        placeholder="Название"
                        {...register(`translations.${lang}.name`)}
                      />
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">
                          Короткое описание
                        </Label>
                        <TranslateButton
                          sourceText={
                            watch("translations.ru.short_description") ?? ""
                          }
                          targetLang={lang as "uz" | "en"}
                          onTranslated={(text) =>
                            setValue(
                              `translations.${lang}.short_description`,
                              text,
                              { shouldDirty: true },
                            )
                          }
                        />
                      </div>
                      <Textarea
                        placeholder="Короткое описание"
                        {...register(`translations.${lang}.short_description`)}
                        rows={2}
                      />
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">
                          Полное описание
                        </Label>
                        <TranslateButton
                          sourceText={watch("translations.ru.description") ?? ""}
                          targetLang={lang as "uz" | "en"}
                          onTranslated={(text) =>
                            setValue(`translations.${lang}.description`, text, {
                              shouldDirty: true,
                            })
                          }
                        />
                      </div>
                      <Suspense fallback={<Skeleton className="h-48" />}>
                        <RichEditor
                          value={watch(`translations.${lang}.description`) ?? ""}
                          onChange={(html) =>
                            setValue(`translations.${lang}.description`, html, {
                              shouldDirty: true,
                            })
                          }
                          placeholder={`Полное описание (${lang.toUpperCase()})`}
                        />
                      </Suspense>
                    </div>
                  ))}
              </div>

              {/* 3. Параметры товара */}
              <div className="space-y-3">
                <Label className="font-semibold">3. Параметры товара</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>SKU *</Label>
                    <Input {...register("sku")} />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      Slug *
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground"
                        title={
                          slugLocked
                            ? "Включить автогенерацию из названия"
                            : "Заморозить ручное значение"
                        }
                        onClick={() => setSlugLocked((v) => !v)}
                      >
                        {slugLocked ? (
                          <Lock className="w-3 h-3" />
                        ) : (
                          <Unlock className="w-3 h-3" />
                        )}
                      </button>
                    </Label>
                    <Input
                      value={watch("slug") ?? ""}
                      onChange={(e) => {
                        setValue("slug", e.target.value, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                        setSlugLocked(true);
                      }}
                      className="font-mono"
                    />
                    <p className="text-[10px] text-muted-foreground">
                      {slugLocked
                        ? "Заморожен — переключите замок, чтобы вернуть автогенерацию из названия"
                        : "Генерируется автоматически из названия"}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Код 1С</Label>
                    <Input
                      value={watch("code_1c") ?? ""}
                      onChange={(e) =>
                        setValue("code_1c", e.target.value, {
                          shouldDirty: true,
                        })
                      }
                      placeholder="напр. 1509"
                      className="font-mono"
                    />
                    <p className="text-[10px] text-muted-foreground">
                      Код номенклатуры в 1С «Далион». По нему ИИ-агент и будущая
                      интеграция сопоставляют товар с выгрузкой — должен быть
                      уникальным.
                    </p>
                  </div>
                  <div className="space-y-2" />
                  <div className="col-span-2 space-y-2">
                    <Label>Основная категория *</Label>
                    <PrimaryCategoryPicker
                      value={watch("category_id") ?? null}
                      onChange={(id) =>
                        setValue("category_id", id, { shouldDirty: true })
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      SEO-canonical URL и хлебные крошки берутся отсюда.
                    </p>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Дополнительные категории</Label>
                    <SecondaryCategoriesPicker
                      primaryId={watch("category_id") ?? null}
                      value={watch("secondary_category_ids") ?? []}
                      onChange={(ids) =>
                        setValue("secondary_category_ids", ids, {
                          shouldDirty: true,
                        })
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Товар будет показан в этих категориях тоже, но canonical
                      URL остаётся через основную — без дублирования для SEO.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Порядок сортировки</Label>
                    <Input type="number" {...register("sort_order")} />
                  </div>
                  <div className="space-y-2">
                    <Label>Остаток на складе</Label>
                    <Input type="number" {...register("stock_quantity")} />
                    {productId && (
                      <a
                        href={`/stock-log?product_id=${productId}`}
                        className="text-xs text-primary hover:underline inline-block"
                      >
                        {t("stockLog.viewHistory")}
                      </a>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Вес (граммы)</Label>
                    <Input type="number" {...register("weight_grams")} />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={watch("is_active")}
                    onCheckedChange={(v) => setValue("is_active", v, { shouldDirty: true })}
                  />
                  <Label>Активен (виден в каталоге)</Label>
                </div>
              </div>

              {/* 4. Цена */}
              <div className="space-y-3">
                <Label className="font-semibold">4. Цена</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Себестоимость</Label>
                    <PriceInput
                      value={watch("cost_price")}
                      onChange={(v) => setValue("cost_price", v, { shouldDirty: true })}
                    />
                    <p className="text-[10px] text-muted-foreground">
                      Нужна для автоцены — не даёт продать в убыток.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Цена по акции</Label>
                    <PriceInput
                      value={watch("sale_price")}
                      onChange={(v) => setValue("sale_price", v, { shouldDirty: true })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Базовая цена *</Label>
                    <PriceInput
                      value={watch("base_price")}
                      onChange={(v) => setValue("base_price", v ?? 0, { shouldDirty: true })}
                      disabled={watch("auto_price_enabled")}
                    />
                    {watch("auto_price_enabled") && (
                      <p className="text-[10px] text-muted-foreground">
                        Пересчитывается автоматически каждую ночь по ценам
                        конкурентов — правка вручную заблокирована.
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2 p-3 border rounded-md">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={watch("auto_price_enabled")}
                      onCheckedChange={(v) =>
                        setValue("auto_price_enabled", v, { shouldDirty: true })
                      }
                    />
                    <Label>От цен конкурента −%</Label>
                  </div>

                  {watch("auto_price_enabled") && (
                    <>
                      <div className="flex items-center gap-3 pl-9">
                        <div className="w-44 space-y-1">
                          <Label className="text-xs text-muted-foreground">
                            Конкурент
                          </Label>
                          <Select
                            value={watch("competitor_price_source") ?? undefined}
                            onValueChange={(v) =>
                              setValue(
                                "competitor_price_source",
                                v as "texnomart" | "mediapark" | "castore" | "idea",
                                { shouldDirty: true },
                              )
                            }
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Выберите…" />
                            </SelectTrigger>
                            <SelectContent>
                              {COMPETITOR_STORES.map((s) => (
                                <SelectItem key={s.code} value={s.code}>
                                  {s.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">%</Label>
                          <Input
                            type="number"
                            min={0}
                            max={90}
                            className="w-24 h-9"
                            value={watch("competitor_discount_percent") ?? ""}
                            onChange={(e) =>
                              setValue(
                                "competitor_discount_percent",
                                e.target.value === "" ? null : Number(e.target.value),
                                { shouldDirty: true },
                              )
                            }
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground pl-9">
                        Цена = цена выбранного конкурента минус %, округлено до
                        ближайшей 1000 сум (но не ниже себестоимости + 5%)
                      </p>
                      {productId && (
                        <div className="pl-9 space-y-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={repriceMutation.isPending || formState.isDirty}
                            onClick={() => repriceMutation.mutate()}
                          >
                            {repriceMutation.isPending ? (
                              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                            ) : (
                              <RefreshCw className="w-3.5 h-3.5 mr-1" />
                            )}
                            Обновить цены конкурентов и пересчитать
                          </Button>
                          {formState.isDirty && (
                            <p className="text-[10px] text-warning">
                              Сначала сохраните товар — пересчёт идёт по
                              сохранённым настройкам, не по черновику формы.
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="images" className="pt-4">
              {productId && (
                <ImageUploader
                  productId={productId}
                  images={product?.images ?? []}
                />
              )}
            </TabsContent>

            <TabsContent value="variants" className="pt-4">
              {productId && (
                <VariantsEditor
                  productId={productId}
                  productSku={product?.sku}
                  variants={product?.variants ?? []}
                />
              )}
            </TabsContent>

            <TabsContent value="attributes">
              {productId && <AttributesTab productId={productId} />}
            </TabsContent>

            <TabsContent value="stock" className="pt-4">
              {productId && <StoreStockEditor productId={productId} />}
            </TabsContent>

            <TabsContent value="prices" className="pt-4">
              {productId && <CompetitorPricesTab productId={productId} />}
            </TabsContent>

            <TabsContent value="metadata" className="pt-4 space-y-2">
              <Label>Метаданные (JSON)</Label>
              <Textarea
                value={JSON.stringify(watch("metadata") ?? {}, null, 2)}
                onChange={(e) => {
                  try {
                    setValue("metadata", JSON.parse(e.target.value), {
                      shouldDirty: true,
                    });
                  } catch {
                    /* invalid JSON — ignore */
                  }
                }}
                rows={10}
                className="font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground">
                Характеристики товара (цвет, материал, размеры и т.д.)
              </p>
            </TabsContent>
          </Tabs>

          <div className="flex border-t pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate("/products")}
            >
              {t("common.back")}
            </Button>
          </div>

          <StickySaveBar
            dirty={formState.isDirty}
            saving={saveMutation.isPending}
            onDiscard={() => reset()}
          />
        </form>
      </CardContent>
    </Card>
  );
}
